/**
 * Reading a customer's item list out of a PDF's text, with no model and no server.
 *
 * ---- why this is not the spreadsheet problem ----
 *
 * A sheet has a grid: find the header, read the columns. PDF text extraction throws the grid away
 * and hands back lines. Both real requisition PDFs on this bench (2026-08-12) prove it, and they
 * do not even agree with each other:
 *
 *   `Purchase Requisition Note` — columnar, unit BEFORE quantity, with a stock column after:
 *       SN Item No  Description                                  UOM Quantity  Present Stock
 *       1 40-02357  ALUMINIUM CHEQUERED PLATE 3 MM THICKNESS 4' X
 *       8' NOS 1 0                                    ← the SAME item, wrapped mid-description
 *
 *   `HAYA Deck Requisition` — a numbered list, quantity BEFORE unit, no columns at all:
 *       1. Printer INK (one each of each color) 4 pcs
 *       2. GMDSS VHF Radio SPARE BATTERY 3 pcs
 *
 * So column detection cannot work here. What both share is that every item line carries a
 * QUANTITY beside a UNIT OF MEASURE, and starts with a line number. That pairing is the anchor.
 *
 * ---- the guards matter more than the parsing ----
 *
 * A requisition PDF is full of numbers that are not quantities: `Tel : +880-31710525, 6674`,
 * `DATE : 06/08/2026`, `REV. NO: 11/2021`. Matching loosely would put a phone number in a customer's
 * order. So a line is only an item when it begins with a line number AND pairs a number with a
 * recognised unit — and anything without both is left alone.
 *
 * Returning `null` rather than an empty list is deliberate, exactly as in `sheetExtract.ts`: a
 * scanned PDF has no text layer at all, and "0 items read confidently" must never be mistaken for
 * "this requisition is empty".
 */

import type { SheetItem } from './sheetExtract'

/**
 * Units seen on real marine requisitions. Deliberately a CLOSED list: it is the guard that stops a
 * date or a phone number being read as a quantity, so widening it weakens the parse. Add only what
 * has been observed in a real document.
 */
/*
  Single letters (`l`, `m`) and short words (`no`, `pc`, `ea`) are DELIBERATELY absent. They match
  inside real descriptions — "ROPE 12 M LONG 4 pcs" would be read as 12 metres of rope — and a
  false quantity is worse than a missed one, because nobody re-reads a line that looks right.
*/
const UOMS = [
  'pcs',
  'nos',
  'set',
  'sets',
  'kg',
  'kgs',
  'ltr',
  'ltrs',
  'mtr',
  'mtrs',
  'box',
  'boxes',
  'ctn',
  'roll',
  'rolls',
  'pkt',
  'pack',
  'packs',
  'bag',
  'bags',
  'can',
  'cans',
  'tube',
  'tubes',
  'pair',
  'pairs',
  'unit',
  'units',
  'each',
  'drum',
  'drums',
  'sheet',
  'sheets',
  'bottle',
  'bottles',
  'month',
] as const

const UOM_PATTERN = UOMS.join('|')

/** `1.` / `1)` / `1 ` — the line number every one of these forms puts first. */
const STARTS_ITEM = /^\s*(\d{1,3})[.)]?\s+\S/

/**
 * `… 4 pcs` — quantity then unit, as the HAYA requisition writes it.
 *
 * NOT anchored to the end of the block. PDF text runs the footer straight on after the last item
 * ("… 3 pcs Requisitioner : Ch Off Captain: …"), so an end-anchored pattern matches nothing at all.
 * The FIRST quantity/unit pair after the description is the item; whatever follows is page furniture.
 */
const QTY_THEN_UOM = new RegExp(`^(.*?)\\s+(\\d+(?:\\.\\d+)?)\\s*(${UOM_PATTERN})\\b`, 'i')

/**
 * `… NOS 1 0` — unit then quantity, as the Safeen note writes it, with a trailing stock column.
 * The stock figure is optional and discarded: it is what the vessel HAS, not what it wants, and
 * reading it as the order is the same mistake `ROB` invites on the spreadsheets.
 */
const UOM_THEN_QTY = new RegExp(`^(.*?)\\s+(${UOM_PATTERN})\\s+(\\d+(?:\\.\\d+)?)\\b`, 'i')

/** An item code as these forms write it: `40-02357`, `MSC0119`. Never invented — absent stays null. */
const LEADING_CODE = /^([A-Z0-9]{2,}(?:-[A-Z0-9]+)+|[A-Z]{2,}\d{3,})\s+(.*)$/

/**
 * Glue wrapped lines back together.
 *
 * PDF extraction breaks a long description wherever the page did — the Safeen note splits one item
 * across `… 4' X` and `8' NOS 1 0`. A line that does not START a new item belongs to the one above,
 * so blocks are accumulated from one line number to the next.
 */
function itemBlocks(text: string): string[] {
  const lines = text
    .split('\n')
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .filter((l) => l !== '')

  const blocks: string[] = []
  for (const line of lines) {
    if (STARTS_ITEM.test(line) || blocks.length === 0) blocks.push(line)
    else blocks[blocks.length - 1] += ` ${line}`
  }
  return blocks
}

/** Rejects the numbers that are not quantities. */
function looksLikeNoise(description: string): boolean {
  if (description.length < 3) return true
  if (!/[a-z]/i.test(description)) return true // a row of figures is a total, not an item
  if (/\b(tel|phone|fax|e-?mail|date|rev\.? no|page)\b/i.test(description)) return true
  if (/@/.test(description)) return true
  return false
}

/**
 * The items in a PDF's extracted text, or `null` when none can be read confidently.
 *
 * `null` covers the case that matters most: a SCANNED requisition, which extracts to no text at
 * all and which nothing client-side can read. The caller must say so rather than create an empty
 * enquiry.
 */
export function readItemsFromPdfText(text: string): SheetItem[] | null {
  const items: SheetItem[] = []

  for (const block of itemBlocks(text)) {
    const start = STARTS_ITEM.exec(block)
    if (!start) continue

    const rowNo = start[1]
    const rest = block.slice(start[0].length - 1).trim()

    // Quantity-then-unit first: it is unambiguous. Unit-then-quantity can otherwise mis-split a
    // description that happens to end in a unit word.
    const matched = QTY_THEN_UOM.exec(rest) ?? UOM_THEN_QTY.exec(rest)
    if (!matched) continue

    const qtyThenUom = QTY_THEN_UOM.test(rest)
    let description = matched[1].trim()
    const qty = Number(qtyThenUom ? matched[2] : matched[3])
    const uom = (qtyThenUom ? matched[3] : matched[2]).trim()

    if (!Number.isFinite(qty) || qty <= 0) continue

    // A leading `40-02357` is the customer's code, not part of the description.
    let itemCode: string | null = null
    const withCode = LEADING_CODE.exec(description)
    if (withCode) {
      itemCode = withCode[1]
      description = withCode[2].trim()
    }

    if (looksLikeNoise(description)) continue

    items.push({ description, qty, uom, item_code: itemCode, row_no: rowNo })
  }

  return items.length > 0 ? items : null
}
